'use client';

import { forwardRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { designTokens } from '../theme/tokens';

type Variant = 'primary' | 'secondary' | 'ghost';

const variantStyles: Record<Variant, ButtonHTMLAttributes<HTMLButtonElement>['style']> = {
  primary: {
    background: designTokens.colors.primary,
    color: designTokens.colors.onPrimary,
    border: `1px solid ${designTokens.colors.primary}`,
    boxShadow: 'none',
  },
  secondary: {
    background: designTokens.colors.surfaceContainerLow,
    color: designTokens.colors.primary,
    border: '1px solid transparent',
    backdropFilter: 'none',
  },
  ghost: {
    background: 'transparent',
    color: designTokens.colors.onSurfaceVariant,
    border: '1px solid transparent',
  },
};

export const Button = forwardRef<HTMLButtonElement, { children: ReactNode; variant?: Variant } & ButtonHTMLAttributes<HTMLButtonElement>>(
  function Button({ children, onBlur, onFocus, variant = 'primary', style, ...props }, ref) {
    const [focusVisible, setFocusVisible] = useState(false);

    return (
      <button
        ref={ref}
        {...props}
        onBlur={(event) => {
          setFocusVisible(false);
          onBlur?.(event);
        }}
        onFocus={(event) => {
          setFocusVisible(event.currentTarget.matches(':focus-visible'));
          onFocus?.(event);
        }}
        style={{
          borderRadius: designTokens.radius.base,
          cursor: props.disabled ? 'not-allowed' : 'pointer',
          fontFamily: designTokens.font.sans,
          fontSize: designTokens.typography.labelMd.fontSize,
          fontWeight: designTokens.typography.labelMd.fontWeight,
          lineHeight: designTokens.typography.labelMd.lineHeight,
          minHeight: 44,
          outline: 'none',
          padding: '0 14px',
          transition: 'background 160ms ease, border-color 160ms ease, color 160ms ease, transform 160ms ease',
          ...variantStyles[variant],
          boxShadow: focusVisible ? designTokens.shadows.focusRing : variantStyles[variant]?.boxShadow,
          ...(props.disabled
            ? {
                background: designTokens.colors.surfaceContainer,
                border: `1px solid ${designTokens.colors.outlineVariant}`,
                boxShadow: 'none',
                color: designTokens.colors.outline,
                opacity: 0.72,
              }
            : null),
          ...style,
        }}
      >
        {children}
      </button>
    );
  },
);
